<template>
    <BaseForm>
        <template #fields>
            <TextField
                :value="values.nameShort"
                :label="$t('name-short')"
                :error-messages="errors.nameShort"
                :required="true"
                @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameShort', true)"
            />
            <TextField
                :value="values.nameLong"
                :label="$t('name-long')"
                :error-messages="errors.nameLong"
                :required="true"
                @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameLong', true)"
            />
            <TextAreaField
                :value="values.subtitle"
                :label="$t('subtitle')"
                :error-messages="errors.subtitle"
                :max-length="seriesIdentifiersSchema.shape.subtitle.maxLength ?? undefined"
                @input="setFieldValue('subtitle', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('subtitle', true)"
            />
            <NumberField
                :value="values.executionYear ?? ''"
                :label="$t('execution-year')"
                :error-messages="errors.executionYear"
                @input="setFieldValue('executionYear', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('executionYear', true)"
            />
            <TextAreaField
                :value="values.description"
                :label="$t('description')"
                :error-messages="errors.description"
                :max-length="seriesIdentifiersSchema.shape.description.maxLength ?? undefined"
                @input="setFieldValue('description', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('description', true)"
            />
            <TextAreaField
                :value="values.annualSpecifics"
                :label="$t('annual-specifics')"
                :error-messages="errors.annualSpecifics"
                :max-length="seriesIdentifiersSchema.shape.annualSpecifics.maxLength ?? undefined"
                @input="setFieldValue('annualSpecifics', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('annualSpecifics', true)"
            />
            <FileUpload
                :label="$t('logo')"
                :url="iconUrl"
                :loading="fileUpload.loading.value"
                :clear-button-text="$t('delete-series-icon')"
                @change="uploadIcon('iconUpload', $event)"
                @clear="deleteIcon('iconUpload')"
            />
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import FileUpload from '@/components/Form/FileUpload.vue';
import NumberField from '@/components/Form/NumberField.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import TextField from '@/components/Form/TextField.vue';
import { useEventInjection } from '@/event/composables/useEventInjection';
import { useForm } from '@/forms/composables/useForm';
import { thumbnailUrl } from '@/utils/file';
import { computed } from 'vue';
import { getEventBaseDataSchema } from '../schemas';

const props = defineProps<{ eventId: string }>();

const eventId = computed(() => props.eventId);

const { event, updateEvent } = useEventInjection();

const seriesIdentifiersSchema = computed(() => getEventBaseDataSchema(eventId.value));

const {
    errors,
    values,
    fileUpload,
    setFieldValue,
    setFieldTouched,
    validate,
    onDiscard,
    resetForm,
    uploadIcon,
    deleteIcon,
} = useForm(
    seriesIdentifiersSchema.value,
    computed(() => ({
        nameShort: event.value?.nameShort ?? '',
        nameLong: event.value?.nameLong ?? '',
        subtitle: event.value?.subtitle ?? '',
        executionYear: event.value?.executionYear,
        description: event.value?.description ?? '',
        annualSpecifics: event.value?.annualSpecifics ?? '',
        iconUpload: '',
    })),
);

const iconUrl = computed(
    () =>
        fileUpload.fileUrl.value ?? (values.value.iconUpload === '-' ? '' : thumbnailUrl(event.value?.icon, 'url512')),
);

async function save() {
    const valid = await validate();
    if (valid) {
        await updateEvent({ id: eventId.value, ...values.value });
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
